import useAppDispatch from "../../../../hooks/useAppDispatch";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useRef } from "react";
import { Form, FormInstance } from "antd";
import { useClassMutations } from "../../../../api/mutations";
import { openSuccessMessage } from "../../../../store/message/messageSlice";
import { handleError } from "../../../../helper/errorHandler";
import styles from "./ClassEditPopup.module.scss";
import FormItemInput from "../../../../components/ui/FormItemInput/FormItemInput";
import CustomButton from "../../../../components/ui/CustomButton/CustomButton";
import { IClassForTable, IClassUpdateModel } from "../../../../models/Class";
import FormItemInputNumber from "../../../../components/ui/FormItemInputNumber/FormItemInputNumber";
import euroSvg from "../../../../assets/images/icons/euro.svg";
import FormItemCheckbox from "../../../../components/ui/FormItemCheckbox/FormItemCheckbox";
import FormItemSelect from "../../../../components/ui/FormItemSelect/FormItemSelect";
import { useGetAllClassesCategoriesQuery } from "../../../../api/queries";
import { UNLIMITED_CODE_WORD, UNLIMITED_SLOTS_FOR_CLUBPASS_MEMBERS } from "../../../../constants";
import { timeRanges } from "../../../../helper/timeRanges";

interface IClassEditPopupProps {
  closePopup: () => void;
  item: IClassForTable;
  getData: () => void;
  gymListingId: string;
}

const ClassEditPopup: React.FC<IClassEditPopupProps> = ({
  closePopup,
  item,
  getData,
  gymListingId,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const formRef = useRef<FormInstance>(null);

  console.log("item", item);

  const { updateClass } = useClassMutations();
  const { data: categories = [] } = useGetAllClassesCategoriesQuery(null);

  const categoriesForSelect = useMemo(() => {
    if (!categories.length) return [];
    return categories.map((el) => {
      return {
        id: el.id,
        label: el.name,
      };
    });
  }, [categories]);

  useEffect(() => {
    if (formRef.current && item) {
      formRef.current.setFieldsValue({
        enableClubPass: item.enableClubPass,
        isOpenGymForClubPass: item.isOpenGymForClubPass,
        priceForClubPass: item.priceForClubPass,
        numberSlotsClubPass:
          Number(item.numberSlotsClubPass) === UNLIMITED_SLOTS_FOR_CLUBPASS_MEMBERS
            ? UNLIMITED_CODE_WORD
            : item.numberSlotsClubPass,
        categoryId: item.clubPassCategoryId,
        openGymFromTime: item.openGymFromTime,
        openGymToTime: item.openGymToTime,
      });
    }
  }, [item, formRef.current]);

  const onFinish = async (values: {
    categoryId: string;
    enableClubPass: boolean;
    isOpenGymForClubPass: boolean;
    priceForClubPass: number;
    numberSlotsClubPass: string;
    openGymFromTime: string;
    openGymToTime: string;
  }) => {
    const model: IClassUpdateModel = {
      trainingCourseId: item.id,
      categoryId: values.categoryId,
      enableClubPass: values.enableClubPass,
      isOpenGymForClubPass: values.isOpenGymForClubPass,
      priceForClubPass: values.priceForClubPass,
      numberSlotsClubPass:
        values.numberSlotsClubPass === UNLIMITED_CODE_WORD
          ? UNLIMITED_SLOTS_FOR_CLUBPASS_MEMBERS
          : Number(values.numberSlotsClubPass),
      openGymFromTime: values.openGymFromTime,
      openGymToTime: values.openGymToTime,
    };

    updateClass(
      { id: gymListingId, model },
      {
        onSuccess: () => {
          dispatch(openSuccessMessage(t("ClassUpdatedSuccessfully")));
          closePopup();
          getData();
        },
        onError: (e) => handleError(e, dispatch, t),
      }
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.popup}>
        <h3 className={styles.title}>
          {t("Class")}: <span>{item.name}</span>
        </h3>
        <Form
          className={styles.form}
          ref={formRef}
          name="create-category"
          onFinish={onFinish}
          autoComplete="off"
          layout={"vertical"}
        >
          <FormItemCheckbox
            name={"enableClubPass"}
            label={t("EnableClubPassForThisClass")}
            rules={{ required: true }}
          />

          <FormItemCheckbox
            name={"isOpenGymForClubPass"}
            label={t("AvailableForOpenGym")}
            rules={{ required: true }}
          />

          <FormItemSelect
            name={"categoryId"}
            label={t("Category") + " *"}
            rules={{ required: true }}
            items={categoriesForSelect}
          />

          <FormItemInput
            name={"numberSlotsClubPass"}
            label={t("NumberOfSlotsForClubPass") + " *"}
            rules={{ required: true }}
            unlimitedBtnVisible={true}
            setUnlimitedFn={() =>
              formRef.current?.setFieldsValue({
                numberSlotsClubPass: UNLIMITED_CODE_WORD,
              })
            }
          />

          <FormItemInputNumber
            name={"priceForClubPass"}
            label={t("Price") + " *"}
            rules={{ required: true }}
            prefix={euroSvg}
          />

          <div className={styles.row}>
            <FormItemSelect
              name={"openGymFromTime"}
              label={t("OpenGymAvailableFrom") + " *"}
              rules={{ required: true }}
              items={timeRanges}
            />

            <FormItemSelect
              name={"openGymToTime"}
              label={t("OpenGymAvailableTo") + " *"}
              rules={{ required: true }}
              items={timeRanges}
            />
          </div>

          <div className={styles.btnsWrapper}>
            <CustomButton
              text={t("Close")}
              type={"button"}
              typeStyle={"outline"}
              functionOnClick={closePopup}
            />
            <CustomButton text={t("Save")} type={"submit"} typeStyle={"fill"} />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default ClassEditPopup;
